import { useApolloClient } from "@apollo/client";
import {
  DataGrid,
  DebouncedSearchInput,
  ListHeader,
  ModalOpenButton,
  useFormatting,
} from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { useNavigate } from "react-router-dom";
import {
  BuildingsFilters,
  namedOperations,
} from "../../../clients/graphqlTypes.js";
import { ButtonCreate } from "../../commons/button/Button.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import { Page } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import {
  PAGE_LIST_RESULTS_PER_PAGE,
  RESULTS_PER_PAGE_OPTIONS,
} from "../../constants.js";
import { getAddressLabel } from "../../features/addresses/helpers.js";
import { BuildingCreateModal } from "../../features/buildings/modals/BuildingCreateModal.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { BuildingFragment, useBuildingsQuery } from "./Buildings.generated.js";
import { FilterCrmCompanyChips } from "../../features/crm-companies/CrmCompaniesSelect.js";

type Filters = Omit<BuildingsFilters, "limit" | "offset" | "sorting">;

export const Buildings = () => {
  const navigate = useNavigate();
  const { getFormattedDate } = useFormatting();
  const { t } = useTranslate(["Buildings", "Global"]);

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
  } = useFiltersAndPagination<{}, Filters>({}, [], PAGE_LIST_RESULTS_PER_PAGE);

  const client = useApolloClient();
  const query = useBuildingsQuery({
    client,
    variables: {
      limit,
      offset,
      filters,
    },
  });

  const buildings =
    (query.data ?? query.previousData)?.buildings.edges.map(
      e => e.node.building
    ) ?? [];
  const total = (query.data ?? query.previousData)?.buildings.totalCount ?? 0;

  const stateStore = useDataGridStateStore("Buildings");

  return (
    <Page
      title={t("Buildings", {
        ns: "Buildings",
      })}
    >
      <PageContainer>
        <ListHeader
          hideTitleOnMobile
          title={t("Buildings", {
            ns: "Buildings",
          })}
          CreateButton={
            <ModalOpenButton
              Modal={BuildingCreateModal}
              modalProps={{
                handleComplete: (building, buildingRootItemId, handleClose) => {
                  navigate(`/buildings/${building.id}`);
                  handleClose();
                },
                refetchQueries: [namedOperations.Query.Buildings],
              }}
            >
              <ButtonCreate
                title={t("Create new building", {
                  ns: "Buildings",
                })}
              />
            </ModalOpenButton>
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", {
                ns: "Global",
              })}
              defaultValue={filters.searchTerm}
              onChangeSearchTerm={searchTerm =>
                setFilters(state => ({
                  ...state,
                  searchTerm: searchTerm || undefined,
                }))
              }
            />
          }
          FilterChips={
            filters.crmOrganisationId ? (
              <FilterCrmCompanyChips
                label={t("Linked to CRM", { ns: "Buildings" })}
                value={[filters.crmOrganisationId]}
                setValue={value =>
                  setFilters(({ crmOrganisationId, ...state }) => ({
                    ...state,
                  }))
                }
              />
            ) : undefined
          }
          marginBottom={2}
        />
        <DataGrid
          stateStore={stateStore}
          loading={query.loading}
          hideFooter={total === 0}
          columns={[
            {
              field: "title",
              headerName: t("Title", {
                ns: "Buildings",
              }),
              flex: 1,
              minWidth: 100,
              sortable: false,
              renderCell: ({ row: building }: { row: BuildingFragment }) =>
                building.title,
            },
            {
              field: "address",
              headerName: t("Address", {
                ns: "Buildings",
              }),
              flex: 1,
              minWidth: 100,
              sortable: false,
              renderCell: ({ row: building }: { row: BuildingFragment }) =>
                getAddressLabel(building.buildingAddress),
            },
            {
              field: "createdAt",
              headerName: t("Created", {
                ns: "Buildings",
              }),
              flex: 1,
              minWidth: 100,
              sortable: false,
              renderCell: ({ row: building }: { row: BuildingFragment }) =>
                getFormattedDate(building.createdAt),
            },
          ]}
          rows={buildings}
          onRowClick={building => navigate(`/buildings/${building.id}/edit`)}
          paginationModel={paginationModel}
          onPaginationModelChange={newPaginationModel => {
            setPaginationModel(newPaginationModel);
          }}
          pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
          rowCount={total}
        />
      </PageContainer>
    </Page>
  );
};
